import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
    Play,
    FileText,
    HelpCircle,
    ChevronLeft,
    ChevronRight,
    CheckCircle,
    CheckCircle2,
    Circle,
    Layout,
    Clock,
    User,
    ArrowLeft,
    ArrowRight,
    Maximize2,
    Volume2,
    Settings,
    AlertCircle,
    XCircle,
    RotateCcw,
    Award,
    Upload,
    FilePlus
} from 'lucide-react';
import { showError, showSuccess } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './LearningPortal.css';

const LearningPortal = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [currentLesson, setCurrentLesson] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const playerRef = useRef(null);

    // Assessment States
    const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [assessmentResult, setAssessmentResult] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isAutoSubmitting = useRef(false);
    const lastAssessmentId = useRef(null);
    const [showCompletion, setShowCompletion] = useState(false);

    // Assignment Specific States
    const [submissionText, setSubmissionText] = useState('');
    const [submissionFile, setSubmissionFile] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`/api/student/course-content/${courseId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCourse(res.data.course);
                const fetchedLessons = res.data.lessons;
                setLessons(fetchedLessons);

                // State Preservation: Find first incomplete lesson to resume
                const resumeLesson = fetchedLessons.find(l => !l.isCompleted) || fetchedLessons[fetchedLessons.length - 1];
                if (resumeLesson) {
                    setCurrentLesson(resumeLesson);
                    if (['Quiz', 'Assignment', 'Exam'].includes(resumeLesson.contentType)) {
                        lastAssessmentId.current = resumeLesson._id;
                    }
                }
                setLoading(false);
            } catch (err) {
                showError('Access Denied', 'You may not have access to this course yet.');
                navigate('/student-dashboard/courses');
            }
        };
        fetchData();
    }, [courseId, navigate]);

    // Group lessons and calculate unlock status
    const groupedLessons = React.useMemo(() => {
        const modules = course?.modules || [];
        let isPreviousModuleCompleted = true; // First module is unlocked by default

        return modules.map(mod => {
            const modLessons = lessons.filter(l => l.moduleId === mod._id);
            // Module is complete if all lessons are complete AND it has at least one lesson
            const isCompleted = modLessons.length > 0 && modLessons.every(l => l.isCompleted);

            const isUnlocked = isPreviousModuleCompleted;

            // Allow unlocking next module if this one is completed (or empty)
            isPreviousModuleCompleted = isCompleted || modLessons.length === 0;

            return {
                ...mod,
                lessons: modLessons,
                isUnlocked,
                isCompleted
            };
        });
    }, [course, lessons]);

    const handleLessonClick = (lesson) => {
        const mod = groupedLessons.find(m => m._id === lesson.moduleId);
        if (mod && !mod.isUnlocked) {
            showError('Module Locked', 'Please complete the previous module to unlock this content.');
            return;
        }

        // AUTO-SUBMIT QUIZ IF CHANGING LESSON
        if (['Quiz', 'Assignment', 'Exam'].includes(currentLesson?.contentType) && !currentLesson?.isCompleted && !assessmentResult && Object.keys(answers).length > 0) {
            handleAssessmentSubmit(true); // true = silent/auto
        }

        setCurrentLesson(lesson);
        // Reset assessment state for new lesson
        if (['Quiz', 'Assignment', 'Exam'].includes(lesson?.contentType)) {
            setCurrentQuestionIdx(0);
            setAnswers({});
            setAssessmentResult(lesson.submission || null);
            setSubmissionText('');
            setSubmissionFile(null);
            lastAssessmentId.current = lesson._id;
        }
    };

    // Assessment Handlers
    const handleOptionSelect = (questionId, option) => {
        setAnswers(prev => ({ ...prev, [questionId]: option }));
    };

    const handleAssessmentSubmit = async (isAuto = false) => {
        if (isAutoSubmitting.current) return;
        if (!isAuto) setIsSubmitting(true);
        if (isAuto) isAutoSubmitting.current = true;

        try {
            const token = localStorage.getItem('token');
            const targetId = lastAssessmentId.current || currentLesson?._id;

            let res;
            if (currentLesson?.contentType === 'Assignment') {
                const formData = new FormData();
                formData.append('assessmentId', targetId);
                formData.append('submissionText', submissionText);
                if (submissionFile) formData.append('submissionFile', submissionFile);

                res = await axios.post('/api/student/submissions', formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                const formattedAnswers = Object.keys(answers).map(qId => ({
                    questionId: qId,
                    selectedOption: answers[qId]
                }));

                res = await axios.post('/api/student/submissions', {
                    assessmentId: targetId,
                    answers: formattedAnswers
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            if (!isAuto) {
                setAssessmentResult(res.data.submission);
                if (res.data.isCompleted) {
                    showSuccess('Well done!', 'Work submitted and completed.');
                    // Update local lesson state
                    setLessons(prev => prev.map(l => l._id === targetId ? { ...l, isCompleted: true, submission: res.data.submission } : l));
                } else if (currentLesson?.contentType === 'Assignment') {
                    showSuccess('Submitted!', 'Your assignment has been sent for grading.');
                    setLessons(prev => prev.map(l => l._id === targetId ? { ...l, submission: res.data.submission } : l));
                }
            }
        } catch (err) {
            if (!isAuto) showError('Submission Error', 'Failed to save assessment.');
        } finally {
            if (!isAuto) setIsSubmitting(false);
            isAutoSubmitting.current = false;
        }
    };

    // Auto-submit handle on window close
    useEffect(() => {
        const handleUnload = () => {
            if (['Quiz', 'Assignment', 'Exam'].includes(currentLesson?.contentType) && !currentLesson?.isCompleted && !assessmentResult && Object.keys(answers).length > 0) {
                // Auto-submit logic for unload
            }
        };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [currentLesson, answers, assessmentResult]);

    const handleMarkComplete = async () => {
        if (!currentLesson) return;
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/student/progress/complete', {
                courseId,
                lessonId: currentLesson._id
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Update local state
            const updatedLessons = lessons.map(l =>
                l._id === currentLesson._id ? { ...l, isCompleted: true } : l
            );
            setLessons(updatedLessons);

            showSuccess('Great job!', 'Lesson marked as complete.');

            // Check if course is now fully complete
            const allFinished = updatedLessons.every(l => l.isCompleted);
            if (allFinished) {
                setTimeout(() => setShowCompletion(true), 1500);
            }
        } catch (err) {
            showError('Unable to update tracking', 'Could not save your progress at this time.');
        }
    };

    const navigateLesson = (direction) => {
        if (direction === 'next') {
            const isAssessment = ['Quiz', 'Assignment', 'Exam'].includes(currentLesson?.contentType);
            if (isAssessment && !currentLesson?.isCompleted) {
                showError('Assessment Required', 'You must pass the assessment to move to the next lesson.');
                return;
            }
        }
        const currentIndex = lessons.findIndex(l => l._id === currentLesson?._id);
        const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex >= 0 && nextIndex < lessons.length) {
            handleLessonClick(lessons[nextIndex]);
        } else if (nextIndex === lessons.length && direction === 'next') {
            const allComplete = lessons.every(l => l.isCompleted);
            if (allComplete) {
                setShowCompletion(true);
            } else {
                showError('Course Incomplete', 'Please complete all lessons and assessments to finish.');
            }
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="learning-portal-root">
            {/* Top Navigation Bar */}
            <header className="portal-header">
                <div className="header-brand">
                    <button className="back-btn" onClick={() => navigate('/student-dashboard/courses')}>
                        <ArrowLeft size={20} />
                        <span>Dashboard</span>
                    </button>
                    <div className="header-divider"></div>
                    <div className="course-title-block">
                        <span className="course-label">Current Course</span>
                        <h1 className="course-title">{course?.title}</h1>
                    </div>
                </div>

                <div className="header-controls">
                    <div className="instructor-badge">
                        <div className="instructor-avatar">{course?.tutorId?.name?.charAt(0)}</div>
                        <div className="instructor-details">
                            <span className="role">Instructor</span>
                            <span className="name">{course?.tutorId?.name}</span>
                        </div>
                    </div>
                    <button className="toggle-sidebar-btn" onClick={() => setSidebarOpen(!sidebarOpen)} title="Toggle Curriculum">
                        <Layout size={20} />
                    </button>
                </div>
            </header>

            <div className="portal-body">
                {/* Main Content Area */}
                <main className={`portal-main ${!sidebarOpen ? 'expanded' : ''}`}>
                    <div className="content-viewport">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={showCompletion ? 'completion' : currentLesson?._id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                                className="content-wrapper"
                            >
                                {showCompletion ? (
                                    <div className="completion-screen">
                                        <div className="completion-card">
                                            <div className="award-icon-wrapper">
                                                <Award size={64} className="award-icon" />
                                            </div>
                                            <h2>Course Completed!</h2>
                                            <p>You have successfully finished <strong>{course?.title}</strong>. Great dedication!</p>

                                            <div className="completion-stats">
                                                <div className="stat-box">
                                                    <span className="stat-value">{lessons.length}</span>
                                                    <span className="stat-label">Lessons Completed</span>
                                                </div>
                                                <div className="stat-box">
                                                    <span className="stat-value">100%</span>
                                                    <span className="stat-label">Mastery</span>
                                                </div>
                                            </div>

                                            <div className="completion-actions">
                                                <button className="btn-primary" onClick={() => navigate('/student-dashboard/courses')}>
                                                    Return to Dashboard
                                                </button>
                                                <button className="btn-outline" onClick={() => setShowCompletion(false)}>
                                                    Review Material
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : ['Quiz', 'Exam'].includes(currentLesson?.contentType) ? (
                                    <div className="assessment-container">
                                        {assessmentResult ? (
                                            <div className="result-card">
                                                <div className={`status-icon ${((assessmentResult.obtainedMarks / assessmentResult.totalMarks) * 100) >= (currentLesson?.passingPercentage || 80) ? 'success' : 'danger'}`}>
                                                    {((assessmentResult.obtainedMarks / assessmentResult.totalMarks) * 100) >= (currentLesson?.passingPercentage || 80) ? <CheckCircle size={64} /> : <XCircle size={64} />}
                                                </div>
                                                <h3>{((assessmentResult.obtainedMarks / assessmentResult.totalMarks) * 100) >= (currentLesson?.passingPercentage || 80) ? 'Assessment Passed' : 'Assessment Failed'}</h3>

                                                <div className="score-display">
                                                    <span className="score-percentage">{((assessmentResult.obtainedMarks / assessmentResult.totalMarks) * 100).toFixed(0)}%</span>
                                                    <span className="score-text">Final Score</span>
                                                </div>

                                                <div className="score-details">
                                                    <div className="detail-item">
                                                        <span>Correct Answers</span>
                                                        <strong>{assessmentResult.obtainedMarks} / {assessmentResult.totalMarks}</strong>
                                                    </div>
                                                    <div className="detail-item">
                                                        <span>Passing Requirement</span>
                                                        <strong>{currentLesson?.passingPercentage || 80}%</strong>
                                                    </div>
                                                </div>

                                                {((assessmentResult.obtainedMarks / assessmentResult.totalMarks) * 100) < (currentLesson?.passingPercentage || 80) && (
                                                    <button className="btn-retry" onClick={() => { setAssessmentResult(null); setCurrentQuestionIdx(0); setAnswers({}); }}>
                                                        <RotateCcw size={18} /> Try Again
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="quiz-engine">
                                                {(!currentLesson.questions || currentLesson.questions.length === 0) ? (
                                                    <div className="empty-state">
                                                        <AlertCircle size={48} />
                                                        <h3>No Questions Available</h3>
                                                        <p>The instructor has not added any questions to this assessment yet.</p>
                                                    </div>
                                                ) : (
                                                    <div className="quiz-card">
                                                        <div className="quiz-header">
                                                            <div className="progress-section">
                                                                <span className="question-tracker">Question {currentQuestionIdx + 1} of {currentLesson.questions.length}</span>
                                                                <div className="progress-bar-bg">
                                                                    <div className="progress-bar-fill" style={{ width: `${((currentQuestionIdx + 1) / currentLesson.questions.length) * 100}%` }}></div>
                                                                </div>
                                                            </div>
                                                            <div className="timer-badge">
                                                                <Clock size={16} />
                                                                <span>{currentLesson.timeLimit || 20} min</span>
                                                            </div>
                                                        </div>

                                                        <div className="question-body">
                                                            <h3 className="question-text">{currentLesson.questions[currentQuestionIdx].questionText}</h3>
                                                            <div className="options-grid">
                                                                {currentLesson.questions[currentQuestionIdx].options.map((opt, idx) => {
                                                                    const isSelected = answers[currentLesson.questions[currentQuestionIdx]._id] === opt;
                                                                    return (
                                                                        <div
                                                                            key={idx}
                                                                            className={`option-item ${isSelected ? 'selected' : ''}`}
                                                                            onClick={() => handleOptionSelect(currentLesson.questions[currentQuestionIdx]._id, opt)}
                                                                        >
                                                                            <div className="option-marker">{String.fromCharCode(65 + idx)}</div>
                                                                            <span className="option-text">{opt}</span>
                                                                            <div className="selection-indicator">
                                                                                {isSelected && <CheckCircle2 size={20} />}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>

                                                        <div className="quiz-footer">
                                                            <button
                                                                className="btn-nav-quiz"
                                                                onClick={() => setCurrentQuestionIdx(p => p - 1)}
                                                                disabled={currentQuestionIdx === 0}
                                                            >
                                                                <ArrowLeft size={18} /> Previous
                                                            </button>

                                                            {currentQuestionIdx === currentLesson.questions.length - 1 ? (
                                                                <button className="btn-submit-quiz" onClick={() => handleAssessmentSubmit()} disabled={isSubmitting}>
                                                                    {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
                                                                    {!isSubmitting && <CheckCircle size={18} />}
                                                                </button>
                                                            ) : (
                                                                <button className="btn-next-quiz" onClick={() => setCurrentQuestionIdx(p => p + 1)}>
                                                                    Next <ArrowRight size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : currentLesson?.contentType === 'Assignment' ? (
                                    <div className="assessment-container">
                                        {assessmentResult ? (
                                            <div className="assignment-status-card">
                                                <div className={`status-icon ${assessmentResult.status === 'Graded' ? 'success' : 'pending'}`}>
                                                    {assessmentResult.status === 'Graded' ? <CheckCircle size={64} /> : <Clock size={64} />}
                                                </div>
                                                <h3>Assignment {assessmentResult.status}</h3>
                                                <p className="status-text">
                                                    {assessmentResult.status === 'Graded'
                                                        ? `Your work has been graded. Score: ${assessmentResult.obtainedMarks}/${assessmentResult.totalMarks}`
                                                        : 'Your work has been submitted and is currently awaiting review by the tutor.'}
                                                </p>

                                                <div className="submission-preview">
                                                    <div className="preview-item">
                                                        <span>Submitted On</span>
                                                        <strong>{new Date(assessmentResult.createdAt).toLocaleDateString()}</strong>
                                                    </div>
                                                    {assessmentResult.submissionFileUrl && (
                                                        <div className="preview-item">
                                                            <span>Delivered File</span>
                                                            <a href={`${API_URL}${assessmentResult.submissionFileUrl}`} target="_blank" rel="noopener noreferrer" className="file-link">
                                                                View Submission <Maximize2 size={14} />
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>

                                                {assessmentResult.tutorFeedback && (
                                                    <div className="feedback-section">
                                                        <h4>Tutor Feedback</h4>
                                                        <div className="feedback-content">
                                                            {assessmentResult.tutorFeedback}
                                                        </div>
                                                    </div>
                                                )}

                                                {assessmentResult.status !== 'Graded' && (
                                                    <button className="btn-retry" onClick={() => setAssessmentResult(null)}>
                                                        Update Submission
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="assignment-uploader">
                                                <div className="assignment-header">
                                                    <div className="header-info">
                                                        <h3>Submit Your Work</h3>
                                                        <p>Fill in the required details and upload your assignment file.</p>
                                                    </div>
                                                    <div className="type-badge assignment">Assignment</div>
                                                </div>

                                                <div className="uploader-body">
                                                    <div className="input-group">
                                                        <label>Submission Notes</label>
                                                        <textarea
                                                            placeholder="Describe your work, steps taken, or any specific details for the tutor..."
                                                            value={submissionText}
                                                            onChange={(e) => setSubmissionText(e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="file-upload-zone">
                                                        <input
                                                            type="file"
                                                            id="assignmentFile"
                                                            onChange={(e) => setSubmissionFile(e.target.files[0])}
                                                            hidden
                                                        />
                                                        <label htmlFor="assignmentFile" className="upload-label">
                                                            {submissionFile ? (
                                                                <div className="file-selected">
                                                                    <FileText size={48} />
                                                                    <span>{submissionFile.name}</span>
                                                                    <button className="change-btn" onClick={(e) => { e.preventDefault(); setSubmissionFile(null); }}>Change File</button>
                                                                </div>
                                                            ) : (
                                                                <div className="upload-placeholder">
                                                                    <Upload size={40} />
                                                                    <span>Click to upload assignment file (PDF, ZIP, DOCX)</span>
                                                                    <span className="sub-text">Max size: 100MB</span>
                                                                </div>
                                                            )}
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="uploader-footer">
                                                    <button className="btn-submit-assignment" onClick={() => handleAssessmentSubmit()} disabled={isSubmitting}>
                                                        {isSubmitting ? 'Uploading...' : 'Submit Assignment'}
                                                        {!isSubmitting && <CheckCircle size={18} />}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : currentLesson?.contentType === 'Video' ? (
                                    <div className="video-player-container">
                                        <video
                                            ref={playerRef}
                                            src={`${API_URL}${currentLesson.mediaUrl}`}
                                            controls
                                            className="video-element"
                                            poster={`${API_URL}${course?.thumbnailUrl}`}
                                        />
                                    </div>
                                ) : currentLesson?.contentType === 'Document' ? (
                                    <div className="document-viewer">
                                        <iframe
                                            src={`${API_URL}${currentLesson.mediaUrl}`}
                                            title="Document Viewer"
                                            className="document-iframe"
                                        />
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <div className="icon-wrapper"><Play size={32} /></div>
                                        <p>Select a lesson to begin learning.</p>
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Meta Controls (Bottom Bar) */}
                    <div className="content-meta-bar">
                        <div className="meta-info">
                            <span className="content-type-badge">
                                {currentLesson?.contentType === 'Video' ? <Play size={14} /> : <FileText size={14} />}
                                {currentLesson?.contentType}
                            </span>
                            <h2 className="meta-title">{currentLesson?.title || currentLesson?.lessonTitle}</h2>
                            <p className="meta-description">{currentLesson?.description || currentLesson?.lessonDescription}</p>
                        </div>
                        <div className="meta-controls">
                            <button className="btn-icon" onClick={() => navigateLesson('prev')} disabled={lessons.findIndex(l => l._id === currentLesson?._id) === 0}>
                                <ChevronLeft size={24} />
                            </button>

                            {/* UPDATED: Mark Complete Button Logic */}
                            {!currentLesson?.isCompleted && (
                                (!['Quiz', 'Assignment', 'Exam'].includes(currentLesson?.contentType) ||
                                    (assessmentResult && ((assessmentResult.obtainedMarks / assessmentResult.totalMarks) * 100) >= (currentLesson?.passingPercentage || 80)))
                            ) && (
                                    <button className="btn-mark-complete" onClick={handleMarkComplete}>
                                        <CheckCircle size={18} /> Mark Complete
                                    </button>
                                )}

                            <button
                                className="btn-next-lesson"
                                onClick={() => navigateLesson('next')}
                                disabled={
                                    lessons.findIndex(l => l._id === currentLesson?._id) === lessons.length - 1 ||
                                    (['Quiz', 'Assignment', 'Exam'].includes(currentLesson?.contentType) && !currentLesson?.isCompleted)
                                }
                            >
                                Next Lesson <ChevronRight size={20} />
                            </button>
                        </div>
                    </div>
                </main>

                {/* Light Professional Sidebar */}
                <aside className={`portal-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
                    <div className="sidebar-header">
                        <h3>Course Content</h3>
                    </div>
                    <div className="sidebar-content">
                        {groupedLessons.map((module, mIdx) => (
                            <div key={module._id} className="curriculum-module">
                                <div className={`module-header ${!module.isUnlocked ? 'locked' : ''}`}>
                                    <div className="module-title-row">
                                        <span className="module-index">Section {mIdx + 1}</span>
                                        {!module.isUnlocked && <HelpCircle size={14} title="Complete previous section to unlock" />}
                                    </div>
                                    <h4 className="module-title">{module.moduleTitle}</h4>
                                </div>

                                <div className="module-lessons">
                                    {module.lessons.map((lesson, idx) => (
                                        <button
                                            key={lesson._id}
                                            className={`lesson-item ${currentLesson?._id === lesson._id ? 'active' : ''} ${lesson.isCompleted ? 'completed' : ''} ${!module.isUnlocked ? 'disabled' : ''}`}
                                            onClick={() => handleLessonClick(lesson)}
                                        >
                                            <div className="lesson-status-icon">
                                                {lesson.isCompleted ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                                            </div>
                                            <div className="lesson-details">
                                                <span className="lesson-name">{idx + 1}. {lesson.title || lesson.lessonTitle}</span>
                                                <div className="lesson-meta">
                                                    {lesson.contentType === 'Video' ? <Play size={12} /> : <FileText size={12} />}
                                                    <span>{lesson.duration || '10'} min</span>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default LearningPortal;